import React from 'react'
import { Skeleton, Divider, Typography, Row, Col, Form, Select, Slider, Switch, Input, Button, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import Iframe from 'react-iframe'
import { Browser as BrowserMockup } from 'react-window-ui'
import { FormComponent, BaseFormComponentProps } from '../../../BaseComponent'
import Config from '../../../../config'
import IShelter from '../../../../interfaces/IShelter'
import ISpecie from '../../../../interfaces/ISpecie'
import IGroup from '../../../../interfaces/IGroup'

type ShelterIframeProps = BaseFormComponentProps & {
  uuid: string,
  data: any,
  loading: boolean,
  contentLoading: boolean,
  error: any | null,
  
  shelter: IShelter | null,
  species: Array<ISpecie> | null,
  groups: Array<IGroup> | null,
  
  loadShelter: (...args: any) => void,
  loadSpecies: (...args: any) => void,
  loadGroups: (...args: any) => void,

  loadShelterSetting: (args: any) => void,
  dispatchSave: (args: any) => void
}

export default class ShelterIframe extends FormComponent<ShelterIframeProps> {

  static defaultProps = {
    loading: false,
    contentLoading: true,
    error: null
  }

  state = {
    loadingSettings: false,
    loaded: !this.props.contentLoading && this.props.data, // Check if our data is already loaded :P
    saving: false,
    preview: {
      branch_uuid: undefined,
      specie_uuid: undefined,
      group_uuid: undefined,
      type_key: undefined,
      g: 15,
      p: 15,
      t: false,
      nb: false,
      al: false
    },
    compact: false,
  }

  static getDerivedStateFromProps(props: any, state: any) {
    if (!props.data && !state.loaded && !state.loadingSettings && !props.contentLoading && !props.loading) {
      state.loadingSettings = true

      props.loadShelterSetting({
        search: {
          organization_shelter: {
            has: {
              uuid: props.uuid
            }
          }
        }
      })

      return state
    }

    if (!props.loading && !state.loaded /*&& state.loadingSettings*/) {
      state.loaded = true
      state.loadingSettings = false

      if (props.data) {
        state.preview = props.data.value
      }
    }

    if (state.loaded && state.saving && !props.loading) {
      state.saving = false

      if (!props.error) {
        message.success('Iframe settings saved!')
      } else {
        message.error(`Could not save iframe settings! ${props.error}`, 5)
      }
    }

    return state
  }

  componentDidMount() {
    const { uuid, shelter, species, groups, loadShelter, loadSpecies, loadGroups } = this.props

    // Validate permissions
    this.validateAccessPermissions()

    if (!shelter) {
      loadShelter({
        search: {
          uuid: uuid,
          contract_options: {
            with: true
          },
          organization_question_shelters: {
            with: {
              deleted: false,
              kiosk_reason_id: null,
              question_shelter_species: {
                with: true
              }
            }
          },
          organization_branches: {
            with: true
          }
        },
        options: {
          _force_ids: true
        }
      })
    }

    if (!species) {
      loadSpecies({
        options: {
          limit:9999
        }
      })
    }

    if (!groups) {
      loadGroups({
        options: {
          limit:9999
        }
      })
    }
  }

  handlePreviewFieldChange = (field: string, value: any) => {
    this.setState((state: any) => ({
      ...state,
      preview: {
        ...state.preview,
        [field]: value
      }
    }))
  }

  handleCopy = (withScript = false) => {
    const value = this.mountUrl()

    if (!value || value.length === 0) {
      return
    }

    if (!navigator.clipboard) {
      message.error('Failed to copy')
      return
    }

    if (withScript) {
      const script = `<script src="https://iframe.adopets.com/iframe.js"></script> <iframe id="adopets-iframe" src="${value}" width="100%" frameborder="0"></iframe>`
      navigator.clipboard.writeText(script)
    } else {
      navigator.clipboard.writeText(value)
    }
    message.success('Copied to clipboard')
  }

  handleSave = (values: any) => {
    const { uuid, dispatchSave } = this.props
    let { iframe_settings } = values

    // Escape null fields
    Object.keys(iframe_settings).forEach(k => {
      if (iframe_settings[k] === undefined || iframe_settings[k] === null) {
        delete iframe_settings[k]
      }
    })

    const payload = {
      shelter_uuid: uuid,
      type_key: 'IFRAME',
      value: iframe_settings
    }

    dispatchSave(payload)
    this.setState({
      saving: true
    })
  }

  mountUrl = () => {
    const { shelter } = this.props
    const { preview }: any = this.state

    if (!shelter) return Config.iframeBase

    if(this.state.compact){
      let _url = `${Config.iframeBase}${this.state.compact ? '/compact' : ''}/${shelter.slug}?shelter_uuid=${shelter.uuid}`
      return _url
    }

    let url = `${Config.iframeBase}/${shelter.slug}?shelter_uuid=${shelter.uuid}`

    Object.keys(preview).forEach(key => {
      let field = preview[key]

      if (field !== undefined && field !== null) {
        if (field instanceof Array) {
          field = field.join('|')
        }

        if (!!field) url += `&${key}=${field}`
      }
    })

    return url
  }

  render() {
    const { loading, contentLoading, data, shelter, species, groups } = this.props
    const { loaded } = this.state

    if (contentLoading || !loaded) {
      return <Skeleton />
    }

    const branchOptions = shelter?.organization_branches?.map(branch => {
      return {
        label: branch.name,
        value: branch.uuid
      }
    }) as any

    const specieOptions = species?.map(specie => {
      return {
        label: specie.name,
        value: specie.uuid
      }
    }) as any

    const groupOptions = groups?.map(group => {
      return {
        label: group.name,
        value: group.uuid
      }
    }) as any

    const careStatusOptions: any = [
      {
        label: 'All',
        value: null
      },
      {
        label: 'In Foster',
        value: true
      },
      {
        label: 'In Shelter',
        value: false
      }
    ]

    const sexOptions = [
      {
        value: 'FEMALE',
        label: 'Female'
      },
      {
        value: 'MALE',
        label: 'Male'
      },
      {
        value: 'UNKNOWN',
        label: 'Unknown'
      }
    ]

    const ageOptions = [
      {
        value: 'ADULT',
        label: 'Adult'
      },
      {
        value: 'BABY',
        label: 'Baby'
      },
      {
        value: 'SENIOR',
        label: 'Senior'
      },
      {
        value: 'YOUNG',
        label: 'Young'
      }
    ]

    const sizeoptions = [
      {
        value: 'XL',
        label: 'Extra Large'
      },
      {
        value: 'XS',
        label: 'Extra Small'
      },
      {
        value: 'L',
        label: 'Large'
      },
      {
        value: 'M',
        label: 'Medium'
      },
      {
        value: 'S',
        label: 'Small'
      }
    ]

    const typeKeyOptions = [
      {
        value: 'ADOPTION',
        label: 'Adoption'
      },
      {
        value: 'LOST',
        label: 'Lost'
      },
      {
        value: 'FOUND',
        label: 'Found'
      }
    ]

    const values = {
      iframe_settings: {
        g: 15,
        p: 15,
        nb: false,
        t: false,
        al: null,
        ...data?.value || {}
      }
    }

    return (
      <div>
        <Typography.Title level={5}>Normal iframe</Typography.Title>
        <Typography.Text>This is the standard iFrame, showing the full library of available animals from the shelter, including filtering capabilities.</Typography.Text>
        <Typography.Title level={5}>Compact iframe</Typography.Title>
        <Typography.Text>This is the compact/short iFrame, showing a single pet on mobile screens, and 4 pets on a desktop screen. On both mobile and desktop version, the compact iframe rotates animals every 30 seconds, cycling through a total of 12 animals. The compact iframe also doesn’t allow users to apply any filters.</Typography.Text>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          marginTop: 20,
          marginBottom: 20
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            width: '200px',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #ddd',
            borderRadius: 4,
            background: this.state.compact ? 'white' : '#e52e6b',
            color: this.state.compact ? 'black' : 'white',
            cursor: 'pointer'
          }}
          onClick={() => {
            this.setState({
              compact: false
            })
          }}>
            Normal
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            width: '200px',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 14,
            border: '1px solid #ddd',
            borderRadius: 4,
            background: !this.state.compact ? 'white' : '#e52e6b',
            color: !this.state.compact ? 'black' : 'white',
            cursor: 'pointer'
          }}
          onClick={() => {
            this.setState({
              compact: true
            })
          }}>
            Compact
          </div>
        </div>
        <Form
          name="shelter_settings"
          layout="vertical"
          initialValues={values}
          onFinish={this.handleSave}
          style={{
            display: this.state.compact ? 'none' : 'flex'
          }}
        >
          <Row gutter={24}>
            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>iFrame Filters</Typography.Title>
              <Typography.Text>This allows you to configure a set of pre-configured iframe filters. After applying the filters below, you can extract the iframe script code to a shelter/rescue organization for use on their website with these filters applied.</Typography.Text>
              <br/><br/>
              <Form.Item
                label="Location"
                name={['iframe_settings', 'branch_uuid']}
              >
                <Select
                  placeholder="Select a Location"
                  mode="multiple"
                  allowClear={true}
                  options={branchOptions}
                  onChange={(value: any) => this.handlePreviewFieldChange('branch_uuid', value)}
                />
              </Form.Item>

              <Form.Item
                label="Species"
                name={['iframe_settings', 'specie_uuid']}
              >
                <Select
                  placeholder="Select a species"
                  mode="multiple"
                  allowClear={true}
                  options={specieOptions}
                  onChange={(value: any) => this.handlePreviewFieldChange('specie_uuid', value)}
                />
              </Form.Item>

              <Form.Item
                label="Group"
                name={['iframe_settings', 'group_uuid']}
              >
                <Select
                  placeholder="Select a group"
                  allowClear={true}
                  options={groupOptions}
                  onChange={(value: any) => this.handlePreviewFieldChange('group_uuid', value)}
                />
              </Form.Item>

              <Form.Item
                label="Care Status"
                name={['iframe_settings', 'al']}
              >
                <Select
                  placeholder="Select a Care Status"
                  allowClear={true}
                  options={careStatusOptions}
                  onChange={(value: any) => this.handlePreviewFieldChange('al', value)}
                />
              </Form.Item>

              <Form.Item
                label="Sex"
                name={['iframe_settings', 'sex']}
              >
                <Select
                  placeholder="Select sex options"
                  mode='multiple'
                  allowClear={true}
                  options={sexOptions}
                  onChange={(v => this.handlePreviewFieldChange('sex', v))}
                />
              </Form.Item>

              <Form.Item
                label="Age"
                name={['iframe_settings', 'age']}
              >
                <Select
                  placeholder="Select age options"
                  mode='multiple'
                  allowClear={true}
                  options={ageOptions}
                  onChange={(v => this.handlePreviewFieldChange('age', v))}
                />
              </Form.Item>

              <Form.Item
                label="Size"
                name={['iframe_settings', 'size']}
              >
                <Select
                  placeholder="Select size options"
                  mode='multiple'
                  allowClear={true}
                  options={sizeoptions}
                  onChange={(v => this.handlePreviewFieldChange('size', v))}
                />
              </Form.Item>

              <Form.Item
                label="Type Key"
                name={['iframe_settings', 'type_key']}
              >
                <Select
                  placeholder="Select a type key"
                  allowClear={true}
                  options={typeKeyOptions}
                  onChange={(v => this.handlePreviewFieldChange('type_key', v))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12}>
              <Typography.Title level={5}>Layout</Typography.Title>

              <Form.Item
                label="Inner padding of the cards"
                name={['iframe_settings', 'g']}
              >
                <Slider
                  onChange={(value: any) => this.handlePreviewFieldChange('g', value)}
                />
              </Form.Item>

              <Form.Item
                label="Page border padding"
                name={['iframe_settings', 'p']}
              >
                <Slider
                  onChange={(value: any) => this.handlePreviewFieldChange('p', value)}
                />
              </Form.Item>

              <Form.Item
                label="Background transparency"
                name={['iframe_settings', 't']}
                valuePropName="checked"
              >
                <Switch
                  onChange={(value: any) => this.handlePreviewFieldChange('t', value)}
                />
              </Form.Item>

              <Form.Item
                label="Hide breed filter"
                name={['iframe_settings', 'nb']}
                valuePropName="checked"
              >
                <Switch
                  onChange={(value: any) => this.handlePreviewFieldChange('nb', value)}
                />
              </Form.Item>

              <Form.Item
                label="Show care status filter"
                name={['iframe_settings', 'al_show']}
                valuePropName="checked"
              >
                <Switch
                  onChange={(value: any) => this.handlePreviewFieldChange('al_show', value)}
                />
              </Form.Item>

              <Button type="primary" htmlType="submit" loading={loading} size="large" ref={(refs) => this.actionRefs.save = refs} block>
                Save
              </Button>
            </Col>
          </Row>
        </Form>

        <Divider />
        <Typography.Title level={5}>Iframe Preview</Typography.Title>
        
        <Typography.Text>Overview on the iframe actions below: </Typography.Text>
        <br/>
        <Typography.Text><b>Copy URL:</b> This option allows you to copy the URL of the iframe and paste it on any browser to evaluate how it looks on a broader page outside this Admin window. Please do not share this URL with the shelter during Onboarding, as they need the script to integrate Adopets Listings into their website.: </Typography.Text>
        <br/>
        <Typography.Text><b>Copy script:</b> Before copying and sharing the script code with the shelter, apply all the iframe filters and configurations. This action allows you to copy the iframe script code for the shelter to integrate Adopets pet listings into their website.: </Typography.Text>
        
        <br/><br/>

        
        <BrowserMockup>
          <Row gutter={8}>
            <Col xs={24} sm={16}>
              <Input value={this.mountUrl()} disabled={true} />
            </Col>

            <Col xs={24} sm={4}>
              <Button type="ghost" style={{width: '100%'}} onClick={() => this.handleCopy()}>
                <CopyOutlined />
                Copy URL
              </Button>
            </Col>

            <Col xs={24} sm={4}>
              <Button type="ghost" style={{width: '100%'}} onClick={() => this.handleCopy(true)}>
                <CopyOutlined />
                Copy Script
              </Button>
            </Col>
          </Row>

          <Divider />

          <Iframe
            url={this.mountUrl()}
            className="iframe-borderless"
            width="100%"
            height="450px"
          />
        </BrowserMockup>
      </div>
    )
  }
}
